import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';

import { Box, Button, Typography } from '@mui/material';
import ErrorHandlingLayout from '@sharedCR/common/ErrorHandlingLayout';
import { AddBoxIcon } from '@sharedCR/coreDesignSystem/icons';
import LabelCDS from '@sharedCR/coreDesignSystem/LabelCDS';
import { LabelSize, LabelVariant } from '@sharedCR/coreDesignSystem/LabelCDS/LabelCDS.constants';
import { TableCDS } from '@sharedCR/coreDesignSystem/Table';
import { LoansPaths } from 'app/configs/routesConfig/loans';
import {
  AutoLockFormsLevelType,
  PropertyAndLoanSubsectionNames,
} from 'app/services/autoLockForms/autoLockForms.constants';
import { runInPendingChangesOperation } from 'app/services/pendingChangesOperation';
import { NoAvailableMessage } from 'app/shared/components/common/NoAvailableMessage';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import { compose } from 'redux';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { ContactTypeExternalProvider } from 'constants/contactTypes';
import useCompanyId from 'hooks/useCompanyId';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoanId from 'hooks/useLoanId';
import usePendingChangesDependentFetch from 'hooks/usePendingChangesDependentFetch';

import { getContactTypeExternalCodesAction } from 'store/contactTypes/actions';
import { selectDriveCodeFamilies } from 'store/contactTypes/selectors';
import { openConfirmationDialogAction, showMessageAction } from 'store/global/actions';
import {
  bulkUpdateLoanContactsAction,
  getDriveReportPreviewAction,
  getLoanContactsViewAction,
  getLoanPartiesAction,
} from 'store/loanApplications/actions';
import {
  selectIsAreaHidden,
  selectIsAreaLocked,
  selectLoanContactsAllowedActions,
  selectLoanContactsView,
} from 'store/loanApplications/selectors';

import EditModal from '../propertyAndLoan/EditModal';
import { getContactsBodyRequestAction } from '../propertyAndLoan/EditModal/Content/Content.utils';
import {
  CONTACTS_TAB_ENTITY_TYPES,
  ENTITY_TYPE_HREF_MAP,
} from '../propertyAndLoan/EditModal/EditModal.hrefs';
import { NEW_RECORD_ID } from '../propertyAndLoan/PropertyAndLoan.contacts';

import DrivePreviewPanel from './DrivePreviewPanel';
import { getPartiesTableConfig } from './Parties.config';
import {
  ADD_CONTACT_BUTTON_LABEL,
  LOADING_ROWS,
  NO_PARTIES_MESSAGE,
  PAGE_TITLE,
  PREVIEW_BUTTON_LABEL,
  PREVIEW_SCROLL_OPTIONS,
  REMOVE_CONTACT_DIALOG_MESSAGE,
  REMOVE_CONTACT_MENU_LABEL,
  REMOVE_CONTACT_SUCCESS_MESSAGE,
} from './Parties.constants';
import {
  headerActionsStyles,
  headerStyles,
  partiesTableStyles,
  rootStyles,
  tablesStyles,
} from './Parties.styles';
import { PartiesTestIds } from './Parties.testIds';
import {
  excludeLoanFileRows,
  getPartiesCountTitle,
  getPartyRows,
  getPreviewIssueSummary,
  getRemoveContactDescription,
  groupPartyRows,
  indexDrivePreview,
} from './Parties.utils';

const Parties = ({ isLoading, setIsLoading }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { applicationId, tab } = useParams();
  const loanId = useLoanId();
  const companyId = useCompanyId();

  const families = useSelector(selectDriveCodeFamilies);
  const contactsView = useSelector(selectLoanContactsView);
  const { isManageAllowed } = useSelector(selectLoanContactsAllowedActions);
  const isContactsAreaHidden = useSelector(
    selectIsAreaHidden(PropertyAndLoanSubsectionNames.CONTACTS, AutoLockFormsLevelType.SUBSECTION),
  );
  const isContactsAreaLocked = useSelector(
    selectIsAreaLocked(PropertyAndLoanSubsectionNames.CONTACTS, AutoLockFormsLevelType.SUBSECTION),
  );

  const [parties, setParties] = useState([]);
  const [preview, setPreview] = useState(null);
  const [isPreviewShown, setIsPreviewShown] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isContactsViewLoaded, setIsContactsViewLoaded] = useState(false);

  const previewRef = useRef(null);

  const rows = useMemo(() => {
    const partyRows = getPartyRows(parties, contactsView);

    return isContactsAreaHidden ? excludeLoanFileRows(partyRows) : partyRows;
  }, [parties, contactsView, isContactsAreaHidden]);
  const groups = useMemo(() => groupPartyRows(rows), [rows]);
  const previewIndex = useMemo(() => indexDrivePreview(preview), [preview]);
  const issueSummary = useMemo(() => getPreviewIssueSummary(preview), [preview]);

  const previewLoanId = loanId ?? applicationId;

  const loadPreview = useCallback(
    ({ silent }) => {
      setIsPreviewLoading(true);

      return dispatch(getDriveReportPreviewAction({ companyId, loanId: previewLoanId, silent }))
        .then(setPreview)
        .catch(Error)
        .finally(() => setIsPreviewLoading(false));
    },
    [companyId, dispatch, previewLoanId],
  );

  const fetchData = useCallback(() => {
    setIsError(false);
    setIsLoading(true);
    loadPreview({ silent: true });

    return Promise.all([
      dispatch(getLoanPartiesAction({ applicationId })).then(setParties),
      dispatch(getLoanContactsViewAction({ applicationId })).then(() =>
        setIsContactsViewLoaded(true),
      ),
    ])
      .catch(() => setIsError(true))
      .finally(() => setIsLoading(false));
  }, [applicationId, dispatch, loadPreview, setIsLoading]);

  const onPreview = useCallback(() => {
    setIsPreviewShown(true);
    previewRef.current?.scrollIntoView(PREVIEW_SCROLL_OPTIONS);
    loadPreview({ silent: false });

    if (!families.length) {
      dispatch(
        getContactTypeExternalCodesAction({
          provider: ContactTypeExternalProvider.DATA_VERIFY_DRIVE,
          silent: true,
        }),
      ).catch(Error);
    }
  }, [dispatch, families.length, loadPreview]);

  const onAddContact = useCallback(() => {
    history.push(
      LoansPaths.applicationManagement.generatePath({
        applicationId,
        tab,
        subtab: ENTITY_TYPE_HREF_MAP.CONTACTS,
        entityId: NEW_RECORD_ID,
      }),
    );
  }, [applicationId, history, tab]);

  const onRemoveContact = useCallback(
    ({ contactId, name }) => {
      const onAccept = () => {
        setIsLoading(true);

        runInPendingChangesOperation(() =>
          dispatch(
            bulkUpdateLoanContactsAction({
              applicationId,
              ...getContactsBodyRequestAction({ form: {}, deletedRecords: [contactId] }),
            }),
          ),
        )
          .then(() => {
            dispatch(
              showMessageAction({ message: REMOVE_CONTACT_SUCCESS_MESSAGE, variant: 'success' }),
            );

            return fetchData();
          })
          .catch(Error)
          .finally(() => setIsLoading(false));
      };

      dispatch(
        openConfirmationDialogAction({
          type: ConfirmationModalType.WARNING,
          message: REMOVE_CONTACT_DIALOG_MESSAGE,
          description: getRemoveContactDescription(name),
          onAcceptButtonName: REMOVE_CONTACT_MENU_LABEL,
          onAccept,
        }),
      );
    },
    [applicationId, dispatch, fetchData, setIsLoading],
  );

  const tableConfig = useMemo(
    () =>
      getPartiesTableConfig({
        drivePreview: { preview, previewIndex },
        applicationId,
        isActionDisabled: isLoading,
        isContactLocked: isContactsAreaLocked,
        onRemove: onRemoveContact,
      }),
    [preview, previewIndex, applicationId, isLoading, isContactsAreaLocked, onRemoveContact],
  );

  usePendingChangesDependentFetch(fetchData);

  useEffectSafeStrictMode(() => {
    if (isPreviewShown) {
      previewRef.current?.scrollIntoView(PREVIEW_SCROLL_OPTIONS);
    }
  }, [isPreviewShown]);

  return (
    <>
      <ErrorHandlingLayout isError={isError} onClick={fetchData}>
        <Box sx={rootStyles}>
          <Box sx={headerStyles}>
            <Typography variant="bold20">{PAGE_TITLE}</Typography>
            {!isLoading && (
              <Typography color="text.secondary" variant="regular14">
                {getPartiesCountTitle(rows.length)}
              </Typography>
            )}
            {!!issueSummary && (
              <LabelCDS
                dataTestId={PartiesTestIds.PREVIEW_SUMMARY}
                size={LabelSize.S}
                title={issueSummary}
                variant={LabelVariant.ORANGE}
              />
            )}
            <Box sx={headerActionsStyles}>
              {isManageAllowed && !isContactsAreaHidden && (
                <Button
                  data-testid={PartiesTestIds.ADD_CONTACT_BUTTON}
                  disabled={isLoading || isContactsAreaLocked}
                  startIcon={<AddBoxIcon />}
                  variant="outlined"
                  onClick={onAddContact}
                >
                  {ADD_CONTACT_BUTTON_LABEL}
                </Button>
              )}
              <Button
                data-testid={PartiesTestIds.PREVIEW_BUTTON}
                disabled={isPreviewLoading || !companyId}
                variant="contained"
                onClick={onPreview}
              >
                {PREVIEW_BUTTON_LABEL}
              </Button>
            </Box>
          </Box>

          <Box data-testid={PartiesTestIds.PARTIES_TABLE} sx={tablesStyles}>
            {isLoading && (
              <TableCDS
                isLoading
                config={tableConfig}
                customStyles={partiesTableStyles}
                data={LOADING_ROWS}
                skeletonRows={6}
              />
            )}

            {!isLoading && !groups.length && <NoAvailableMessage message={NO_PARTIES_MESSAGE} />}

            {!isLoading &&
              groups.map(group => (
                <TableCDS
                  config={tableConfig}
                  customStyles={partiesTableStyles}
                  data={group.rows}
                  key={group.group}
                  title={group.title}
                />
              ))}
          </Box>

          {isPreviewShown && (
            <Box ref={previewRef}>
              <DrivePreviewPanel
                families={families}
                isLoading={isPreviewLoading}
                preview={preview}
              />
            </Box>
          )}
        </Box>
      </ErrorHandlingLayout>

      <EditModal
        entityTypes={CONTACTS_TAB_ENTITY_TYPES}
        isLoading={isLoading || !isContactsViewLoaded}
      />
    </>
  );
};

Parties.propTypes = {
  ...WithLoadingPropTypes,
};

export default compose(withLoading, memo)(Parties);
